import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import BrandLogo from "../components/BrandLogo";

export default function ResetPasswordPage() {
  const [params]      = useSearchParams();
  const navigate      = useNavigate();
  const token         = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(false);

  useEffect(() => { if (!token) navigate("/login", { replace: true }); }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6)  { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm)  { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/password-reset/confirm", { token, new_password: password });
      if (res.data?.ok) { setDone(true); }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid or expired reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden"
      style={{ background: "var(--gradient-hero)" }}>
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl -z-10" />

      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Brand */}
        <div className="flex items-center gap-2.5 mb-8">
          <BrandLogo size={36} />
          <span className="font-extrabold text-lg tracking-tight">Knya</span>
        </div>

        <div className="bg-card rounded-[28px] p-6 border border-border shadow-card">
          {done ? (
            <div className="text-center py-4 space-y-3" data-testid="reset-success">
              <CheckCircle2 size={40} className="text-primary mx-auto" />
              <h2 className="text-xl font-extrabold">Password updated!</h2>
              <p className="text-[13px] text-muted-foreground">Your password has been changed. Sign in with your new password.</p>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="mt-2 w-full h-[50px] rounded-full bg-primary text-primary-foreground font-bold text-[14px] flex items-center justify-center gap-2"
                data-testid="reset-go-to-login"
              >
                Go to sign in <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-[22px] font-extrabold mb-1">Set new password</h2>
              <p className="text-[13px] text-muted-foreground mb-5">Choose a new password for your Knya account.</p>

              <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-password-form">
                <div className="relative">
                  <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="New password (min. 6 chars)"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full h-[50px] rounded-2xl border border-border bg-background pl-10 pr-11 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    data-testid="reset-password-input"
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                <div className="relative">
                  <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="w-full h-[50px] rounded-2xl border border-border bg-background pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    data-testid="reset-confirm-input"
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-[52px] rounded-full bg-primary text-primary-foreground font-bold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99]"
                  data-testid="reset-submit-button"
                >
                  {loading
                    ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <>Update password <ArrowRight size={15} /></>
                  }
                </button>
              </form>
            </>
          )}
        </div>

        <button type="button" onClick={() => navigate("/login")}
          className="mt-5 w-full text-center text-[12px] text-muted-foreground hover:text-foreground transition-colors">
          Back to sign in
        </button>
      </motion.div>
    </div>
  );
}
